import React, { useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import { useViewport } from '@/hooks/useViewport';
import * as Styles from './ActivitiesChart.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
} from 'chart.js';
import { ChartPagination, useChartPaginationContext } from './ChartPagination';
import { IconCalendar, Skeleton } from 'gatorcomponents';
import emptyIconPath from '../../media/empty-state-graphic-bar.svg';
import { useActivitiesChart } from './useActivitiesChart';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
);

const PAGE_SIZE_DESKTOP = 10;
const PAGE_SIZE_MOBILE = 6;
const MAX_CHART_ENTRIES = 30;

function ChartWithPagination({ content }) {
  const { pageItems } = useChartPaginationContext();
  const { chartData, chartOptions } = useActivitiesChart(pageItems, content);
  return (
    <>
      <Styles.ChartContainer>
        <Chart type="bar" data={chartData} options={chartOptions} />
      </Styles.ChartContainer>
      <ChartPagination.Bar />
    </>
  );
}

const ActivitiesChart = ({
  testId = 'activities-chart',
  content,
  data,
  loading = false,
}) => {
  const theme = useContext(ThemeContext);
  const viewport = useViewport();

  const pageSize = useMemo(() => {
    const mdBreakpoint = parseInt(theme.v3.breakpoints.md, 10);
    return viewport.width < mdBreakpoint ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP;
  }, [theme.v3.breakpoints.md, viewport.width]);

  const dataLimited = useMemo(() => (data || []).slice(0, MAX_CHART_ENTRIES), [data]);
  const isEmpty = dataLimited.length === 0;

  if (isEmpty) {
    return (
      <Styles.Wrapper data-testid={testId}>
        <Styles.Header>
          <Styles.TitleAdapter>
            <Styles.Title>{content.title}</Styles.Title>
          </Styles.TitleAdapter>
          <Styles.InfoDate>
            <IconCalendar color="lowLight" size="sm" />
            {content.filter.last30Days}
          </Styles.InfoDate>
        </Styles.Header>
        <Styles.EmptyStateContainer data-testid="empty-state">
          <Styles.EmptyImage src={emptyIconPath} alt={content.emptyImageAlt} />
          <Styles.EmptyTitle>{content.emptyTitle}</Styles.EmptyTitle>
          <Styles.EmptyDescription>{content.emptyDescription}</Styles.EmptyDescription>
        </Styles.EmptyStateContainer>
      </Styles.Wrapper>
    );
  }

  return (
    <Styles.Wrapper data-testid={testId}>
      <Styles.Header>
        <Styles.TitleAdapter>
          <Styles.Title>{content.title}</Styles.Title>
        </Styles.TitleAdapter>
        <Styles.InfoDate>
          <IconCalendar color="lowLight" size="sm" />
          {content.filter.last30Days}
        </Styles.InfoDate>
      </Styles.Header>
      <ChartPagination.Root data={dataLimited} pageSize={pageSize}>
        <ChartWithPagination content={content} />
      </ChartPagination.Root>

      {loading && (
        <Skeleton height="400px" width="100%" />
      )}
    </Styles.Wrapper>
  );
};

export default withErrorBoundary(ActivitiesChart, 'ActivitiesChart');
